import { Cell } from "@highmarch/domain";

/**
 * Where the hub's war camp stands (hubCamp.ts builds it), on the fortress's own isometric grid.
 *
 * The fortress is drawn axis-aligned on the map's cells (its gate faces +z, the hub camera looks along +x+z at its
 * 45° rest yaw), so it reads as an isometric diamond. The camp's first cut (2026-10-22) was laid out in screen-aligned
 * offsets and turned each prop to face the camera, so its palisade ran flat across the screen while the fortress's
 * walls ran on the diagonals — the owner (2026-10-24): "the fortress is at the right isometric angle but the wall
 * isn't". Everything here is in grid offsets from the fortress cell instead — `dx` along world x (up-left on screen),
 * `dz` along world z (up-right, the way the fortress's gate faces) — and every prop is turned in whole quarter turns
 * (`q`), so the walls, the gate, the tents and the yards all sit square on the same grid as the fortress.
 *
 * The camp is the back corner of a compound: the north wall (z = `NorthWall`) with the camp gate on the fortress's
 * gate axis (dx 0) and a gate tower beside it, and the east wall (x = `EastWall`) with a watchtower, meeting in a
 * corner straight up the screen behind the keep. The road leaves the fortress's gate along that axis, passes the camp
 * gate and turns up the screen into the haze — the way the raids come in (hubRaid.ts).
 *
 * Pure data and maths, unit-tested (test/hubCampLayout.test.ts).
 */

/**
 * A prop: model, grid offset from the fortress cell, quarter turns, scale. At `q` 0 an HQ model's front (glTF −z)
 * faces world −z (down-left on screen, toward the camera); each quarter turn is +90° about y (q 1: faces −x,
 * down-right; 2: +z; 3: +x).
 */
export interface CampSpot { readonly name: string; readonly dx: number; readonly dz: number; readonly q?: number; readonly scale?: number }
export type TroopAct = "drill" | "sergeant" | "rest" | "archer" | "patrol" | "watch";
/** A troop: grid offset, what it does, the grid point it faces (`look`) and a patrol's path (grid offsets). */
export interface CampTroop {
  readonly dx: number; readonly dz: number; readonly act: TroopAct;
  readonly look?: readonly [number, number]; readonly path?: readonly (readonly [number, number])[];
}

/** The north wall's line (dz) and the east wall's (dx): far enough out to clear every stand (HubLayout.Stands). */
const NorthWall = 5.5;
const EastWall = 6.1;

export const HubCampLayout = {
  NorthWall,
  EastWall,
  /** The camp gate: on the fortress's gate axis (its gate faces +z), in the north wall. */
  Gate: [0, NorthWall] as const,

  /** Static set dressing (baked). */
  Props: [
    // the north wall: the gate on the fortress's axis, a gate tower beside it, the palisade out to the screen's right
    // edge and back to the corner behind the keep
    { name: "camp_gate", dx: 0, dz: NorthWall },
    { name: "camp_watchtower", dx: -1.75, dz: NorthWall },
    { name: "camp_palisade", dx: -3.4, dz: NorthWall }, { name: "camp_palisade", dx: -5.4, dz: NorthWall }, { name: "camp_palisade", dx: -7.4, dz: NorthWall },
    { name: "camp_palisade", dx: 2.1, dz: NorthWall }, { name: "camp_palisade", dx: 4.1, dz: NorthWall }, { name: "camp_palisade", dx: 5.5, dz: NorthWall },
    // the east wall, from the corner down the screen's left edge, its watchtower half-way
    { name: "camp_palisade", dx: EastWall, dz: 4.5, q: 1 }, { name: "camp_palisade", dx: EastWall, dz: 2.6, q: 1 },
    { name: "camp_watchtower", dx: EastWall, dz: 1.0, q: 1 },
    { name: "camp_palisade", dx: EastWall, dz: -0.6, q: 1 }, { name: "camp_palisade", dx: EastWall, dz: -2.6, q: 1 }, { name: "camp_palisade", dx: EastWall, dz: -4.6, q: 1 },
    // the back corner behind the keep: the commander's pavilion and a soldier's tent
    { name: "camp_tent_command", dx: 3.6, dz: 3.4 },
    { name: "camp_tent", dx: 5.0, dz: 2.9, q: 1 },
    // the tent rows on the left flank, along the east wall
    { name: "camp_tent", dx: 4.8, dz: -3.6, q: 1 }, { name: "camp_tent", dx: 3.2, dz: -4.6 },
    // the drill yard inside the north wall (right of the gate on screen): dummies, a weapon rack
    { name: "camp_dummy", dx: -3.0, dz: 4.5 }, { name: "camp_dummy", dx: -3.9, dz: 4.5 }, { name: "camp_dummy", dx: -4.8, dz: 4.5 },
    { name: "camp_weapon_rack", dx: -5.6, dz: 3.4, q: 1 },
    // the archery butts against the east wall
    { name: "camp_target", dx: 5.3, dz: -0.5, q: 3 }, { name: "camp_target", dx: 5.3, dz: -1.7, q: 3 },
    // supplies: the cart in the drill yard, crates by the gate, barrels on the front flanks
    { name: "camp_cart", dx: -4.2, dz: 2.6, q: 1 },
    { name: "p_crates", dx: 2.2, dz: 4.8 }, { name: "p_barrel_stack", dx: 2.6, dz: -4.0, q: 1 },
    { name: "p_barrel_stack", dx: -5.2, dz: 1.4, scale: 0.9 },
  ] as readonly CampSpot[],

  /** Props whose clips or cloth move (live). */
  LiveProps: [
    { name: "camp_banner", dx: 1.8, dz: 5.0 }, { name: "camp_banner", dx: 5.5, dz: 4.9 },
    { name: "p_campfire", dx: 2.3, dz: 2.6 },
    { name: "p_campfire", dx: 1.5, dz: -5.0, scale: 0.9 },
  ] as readonly CampSpot[],

  Troops: [
    // the drill line facing its dummies, the sergeant behind it
    { dx: -3.0, dz: 3.8, act: "drill", look: [-3.0, 4.5] }, { dx: -3.9, dz: 3.8, act: "drill", look: [-3.9, 4.5] }, { dx: -4.8, dz: 3.8, act: "drill", look: [-4.8, 4.5] },
    { dx: -3.9, dz: 2.8, act: "sergeant", look: [-3.9, 3.8] },
    // round the campfires
    { dx: 2.9, dz: 2.0, act: "rest", look: [2.3, 2.6] }, { dx: 1.7, dz: 2.1, act: "rest", look: [2.3, 2.6] },
    { dx: 2.2, dz: -4.5, act: "rest", look: [1.5, -5.0] }, { dx: 1.0, dz: -5.6, act: "rest", look: [1.5, -5.0] },
    // the archer at the butts
    { dx: 3.4, dz: -2.9, act: "archer", look: [5.3, -1.1] },
    // a patrol along each wall's inside
    { dx: 2.2, dz: 4.95, act: "patrol", path: [[2.2, 4.95], [5.1, 4.95]] },
    { dx: 5.35, dz: 4.2, act: "patrol", path: [[5.35, 4.2], [5.35, 1.9]] },
    // a lookout at the foot of each tower
    { dx: -2.3, dz: 4.85, act: "watch", look: [-2.3, 7] }, { dx: 5.4, dz: 0.3, act: "watch", look: [8, 0.3] },
  ] as readonly CampTroop[],

  /**
   * The road: out of the fortress's gate along its axis, through the camp gate, then turning up the screen into the
   * haze (grid offsets, inside to out). The raids come down it.
   */
  Road: [[0, 1.0], [0, 3.0], [0, NorthWall], [0, 7.0], [1.5, 8.2], [3.5, 9.2], [5.5, 10.2], [8.0, 11.5], [10.5, 12.8]] as readonly (readonly [number, number])[],
  /** Trodden yards: dx, dz, radius (cells). */
  Worn: [
    [-3.9, 4.0, 1.8], [3.0, 3.0, 2.0], [1.5, -4.8, 1.3], [4.3, -1.4, 1.3], [0, 4.9, 1.3], [0, 6.6, 1.2],
  ] as readonly (readonly [number, number, number])[],

  /** World XZ of a grid offset from `base`. */
  toWorld(base: Cell, dx: number, dz: number): { x: number; z: number } { return { x: base.x + dx, z: base.y + dz }; },
  /** The yaw (about y) of `q` quarter turns. */
  yaw(q = 0): number { return (q * Math.PI) / 2; },
  /** Screen-aligned offsets of a grid offset at the rest yaw: `u` cells right, `v` cells behind (HubLayout.Stands). */
  screen(dx: number, dz: number): { u: number; v: number } { return { u: (dz - dx) * Math.SQRT1_2, v: (dx + dz) * Math.SQRT1_2 }; },
  /** Grid offset of a screen-aligned offset (the inverse of `screen`). */
  grid(u: number, v: number): { dx: number; dz: number } { return { dx: (v - u) * Math.SQRT1_2, dz: (u + v) * Math.SQRT1_2 }; },

  /** Distance (cells) from (dx, dz) to the road's centre line. */
  roadDistance(dx: number, dz: number): number {
    let best = Infinity;
    const r = HubCampLayout.Road;
    for (let i = 0; i + 1 < r.length; i++) best = Math.min(best, segmentDistance(dx, dz, r[i][0], r[i][1], r[i + 1][0], r[i + 1][1]));
    return best;
  },
  /** Distance (cells) from (dx, dz) to the nearer wall's line (only along the wall's run). */
  wallDistance(dx: number, dz: number): number {
    return Math.min(segmentDistance(dx, dz, -9, NorthWall, EastWall, NorthWall), segmentDistance(dx, dz, EastWall, NorthWall, EastWall, -6));
  },
} as const;

function segmentDistance(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const vx = bx - ax, vz = bz - az, l2 = vx * vx + vz * vz;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * vx + (pz - az) * vz) / l2)) : 0;
  return Math.hypot(px - ax - vx * t, pz - az - vz * t);
}
